import React, { useState, useEffect, useCallback } from "react";
import {
  Accessibility,
  X,
  RotateCcw,
  Eye,
  Brain,
  Activity,
  Keyboard,
  EyeOff,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  MousePointer2,
  Link2,
  Heading1,
  Pause,
  ImageOff,
  ScanLine,
  Plus,
  Minus,
  ChevronDown,
  FileText,
} from "lucide-react";
import AccessibilityStatementContent from "./AccessibilityStatement";
import {
  applySettings,
  loadStoredSettings,
  persistSettings,
} from "../lib/accessibilitySettings";

const STORAGE_KEY = "cml-a11y-settings";

const DEFAULTS = {
  textSize: "1",          // 1..4
  lineHeight: "0",        // 0 (off), 1, 2, 3
  letterSpacing: "0",     // 0 (off), 1, 2, 3
  readableFont: false,
  align: "default",       // default, left, center, right
  contrast: "default",    // default, dark, light, high
  saturation: "default",  // default, high, low, mono
  highlightLinks: false,
  highlightTitles: false,
  cursor: "default",      // default, big-black, big-white
  stopAnimations: false,
  hideImages: false,
  keyboardNav: false,
  highlightHover: false,
  readingMask: false,
  readingGuide: false,
};

const PROFILES = {
  "seizure-safe": {
    label: "Seizure Safe",
    icon: Activity,
    desc: "Eliminates flashes and reduces color",
    settings: { stopAnimations: true, saturation: "low" },
  },
  "vision-impaired": {
    label: "Vision Impaired",
    icon: Eye,
    desc: "Enhances visuals for low-vision users",
    settings: { textSize: "3", lineHeight: "2", contrast: "high", cursor: "big-black" },
  },
  "adhd-friendly": {
    label: "ADHD Friendly",
    icon: Brain,
    desc: "Reduces distractions, improves focus",
    settings: { readingMask: true, stopAnimations: true, readableFont: true },
  },
  "cognitive": {
    label: "Cognitive Disability",
    icon: Brain,
    desc: "Aids reading & concentration",
    settings: { highlightLinks: true, highlightTitles: true, readableFont: true, lineHeight: "2" },
  },
  "keyboard-nav": {
    label: "Keyboard Navigation",
    icon: Keyboard,
    desc: "Use the website with the keyboard",
    settings: { keyboardNav: true, highlightLinks: true },
  },
  "blind-users": {
    label: "Blind Users (Screen Reader)",
    icon: EyeOff,
    desc: "Optimizes the site for screen readers",
    settings: { readableFont: true, keyboardNav: true },
  },
};

const loadSettings = loadStoredSettings;

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [settings, setSettings] = useState(loadSettings);

  useEffect(() => {
    applySettings(settings);
    persistSettings(settings);
  }, [settings]);

  // Reading mask + guide overlay tracking
  useEffect(() => {
    if (!settings.readingMask && !settings.readingGuide) return;
    const onMove = (e) => {
      document.documentElement.style.setProperty("--mask-y", `${e.clientY}px`);
      document.documentElement.style.setProperty("--guide-y", `${e.clientY}px`);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [settings.readingMask, settings.readingGuide]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const applyProfile = (key) => {
    const p = PROFILES[key];
    if (!p) return;
    setSettings({ ...DEFAULTS, ...p.settings });
  };

  const reset = () => setSettings(DEFAULTS);

  const anyActive = useCallback(() => {
    return Object.keys(DEFAULTS).some((k) => settings[k] !== DEFAULTS[k]);
  }, [settings]);

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open accessibility menu"
        aria-expanded={open}
        data-testid="accessibility-toggle"
        className="a11y-widget-button fixed bottom-4 sm:bottom-6 left-4 sm:left-6 z-[9997] w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#0A192F] text-white shadow-2xl flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors focus:outline-none focus:ring-4 focus:ring-[#D4AF37]/40"
      >
        <Accessibility size={20} className="sm:hidden" />
        <Accessibility size={26} className="hidden sm:block" />
        {anyActive() && (
          <span className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-[#D4AF37] rounded-full border-2 border-white" />
        )}
      </button>

      {/* Overlays */}
      {settings.readingMask && <div className="a11y-reading-mask" aria-hidden="true" />}
      {settings.readingGuide && <div className="a11y-reading-guide" aria-hidden="true" />}

      {/* Panel */}
      {open && (
        <div
          className="a11y-widget-root fixed inset-0 z-[9999] flex"
          role="dialog"
          aria-label="Accessibility menu"
          data-testid="accessibility-panel"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative ml-0 mt-0 mb-0 w-full sm:w-[420px] h-full bg-white shadow-2xl overflow-y-auto flex flex-col">
            {/* Header */}
            <div className="bg-[#0A192F] text-white px-6 py-5 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <Accessibility size={22} className="text-[#D4AF37]" />
                <div>
                  <h2 className="font-display font-bold text-base uppercase tracking-widest">
                    Accessibility
                  </h2>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">
                    WCAG 2.1 AA Adjustments
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close accessibility menu"
                data-testid="accessibility-close"
                className="text-white hover:text-[#D4AF37] p-2"
              >
                <X size={22} />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Reset banner */}
              <div className="flex items-center gap-3">
                <button
                  onClick={reset}
                  data-testid="accessibility-reset"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-[#0A192F] px-4 py-3 text-xs font-display uppercase tracking-widest font-bold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                >
                  <RotateCcw size={14} /> Reset All Settings
                </button>
                <button
                  onClick={() => setShowStatement(true)}
                  data-testid="accessibility-statement-link"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-4 py-3 text-xs font-display uppercase tracking-widest font-bold hover:bg-[#0A192F] hover:text-white transition-colors"
                >
                  <FileText size={14} /> Statement
                </button>
              </div>

              {/* Profiles */}
              <Section title="Accessibility Profiles">
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(PROFILES).map(([key, p]) => {
                    const Icon = p.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => applyProfile(key)}
                        data-testid={`a11y-profile-${key}`}
                        className="text-left border border-gray-200 p-3 hover:border-[#D4AF37] transition-colors group"
                      >
                        <Icon size={20} className="text-[#0A192F] group-hover:text-[#D4AF37]" />
                        <div className="mt-2 font-display font-bold text-xs text-[#0A192F] uppercase tracking-wide">
                          {p.label}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1 leading-snug">{p.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </Section>

              {/* Content adjustments */}
              <Section title="Content Adjustments">
                <StepperControl
                  testId="a11y-text-size"
                  icon={Type}
                  label="Bigger Text"
                  value={parseInt(settings.textSize, 10)}
                  min={1}
                  max={4}
                  onChange={(v) => update({ textSize: String(v) })}
                  labels={["100%", "110%", "125%", "150%"]}
                />
                <StepperControl
                  testId="a11y-line-height"
                  label="Line Height"
                  value={parseInt(settings.lineHeight, 10)}
                  min={0}
                  max={3}
                  onChange={(v) => update({ lineHeight: String(v) })}
                  labels={["Off", "1.6×", "2×", "2.4×"]}
                />
                <StepperControl
                  testId="a11y-letter-spacing"
                  label="Letter Spacing"
                  value={parseInt(settings.letterSpacing, 10)}
                  min={0}
                  max={3}
                  onChange={(v) => update({ letterSpacing: String(v) })}
                  labels={["Off", "Small", "Medium", "Large"]}
                />
                <Toggle
                  testId="a11y-readable-font"
                  icon={Type}
                  label="Readable Font"
                  desc="Switch to a highly readable font (Verdana)"
                  active={settings.readableFont}
                  onClick={() => update({ readableFont: !settings.readableFont })}
                />
                <Toggle
                  testId="a11y-highlight-links"
                  icon={Link2}
                  label="Highlight Links"
                  active={settings.highlightLinks}
                  onClick={() => update({ highlightLinks: !settings.highlightLinks })}
                />
                <Toggle
                  testId="a11y-highlight-titles"
                  icon={Heading1}
                  label="Highlight Headings"
                  active={settings.highlightTitles}
                  onClick={() => update({ highlightTitles: !settings.highlightTitles })}
                />
                <SegmentedControl
                  testId="a11y-align"
                  label="Text Align"
                  value={settings.align}
                  options={[
                    { value: "default", label: "Default", icon: null },
                    { value: "left", label: "Left", icon: AlignLeft },
                    { value: "center", label: "Center", icon: AlignCenter },
                    { value: "right", label: "Right", icon: AlignRight },
                  ]}
                  onChange={(v) => update({ align: v })}
                />
              </Section>

              {/* Color adjustments */}
              <Section title="Color Adjustments">
                <SegmentedControl
                  testId="a11y-contrast"
                  label="Contrast Mode"
                  value={settings.contrast}
                  options={[
                    { value: "default", label: "Default" },
                    { value: "dark", label: "Dark" },
                    { value: "light", label: "Light" },
                    { value: "high", label: "High" },
                  ]}
                  onChange={(v) => update({ contrast: v })}
                />
                <SegmentedControl
                  testId="a11y-saturation"
                  label="Saturation"
                  value={settings.saturation}
                  options={[
                    { value: "default", label: "Default" },
                    { value: "high", label: "High" },
                    { value: "low", label: "Low" },
                    { value: "mono", label: "Mono" },
                  ]}
                  onChange={(v) => update({ saturation: v })}
                />
              </Section>

              {/* Orientation adjustments */}
              <Section title="Orientation Adjustments">
                <SegmentedControl
                  testId="a11y-cursor"
                  label="Cursor"
                  value={settings.cursor}
                  options={[
                    { value: "default", label: "Default", icon: MousePointer2 },
                    { value: "big-black", label: "Big Black" },
                    { value: "big-white", label: "Big White" },
                  ]}
                  onChange={(v) => update({ cursor: v })}
                />
                <Toggle
                  testId="a11y-stop-animations"
                  icon={Pause}
                  label="Stop Animations"
                  desc="Pause autoplay video & disable motion"
                  active={settings.stopAnimations}
                  onClick={() => update({ stopAnimations: !settings.stopAnimations })}
                />
                <Toggle
                  testId="a11y-hide-images"
                  icon={ImageOff}
                  label="Hide Images"
                  active={settings.hideImages}
                  onClick={() => update({ hideImages: !settings.hideImages })}
                />
                <Toggle
                  testId="a11y-keyboard-nav"
                  icon={Keyboard}
                  label="Keyboard Navigation"
                  desc="Enhanced focus indicators"
                  active={settings.keyboardNav}
                  onClick={() => update({ keyboardNav: !settings.keyboardNav })}
                />
                <Toggle
                  testId="a11y-highlight-hover"
                  icon={MousePointer2}
                  label="Highlight Hover"
                  active={settings.highlightHover}
                  onClick={() => update({ highlightHover: !settings.highlightHover })}
                />
                <Toggle
                  testId="a11y-reading-mask"
                  icon={ScanLine}
                  label="Reading Mask"
                  desc="Dim everything outside a reading band"
                  active={settings.readingMask}
                  onClick={() => update({ readingMask: !settings.readingMask })}
                />
                <Toggle
                  testId="a11y-reading-guide"
                  icon={ScanLine}
                  label="Reading Guide"
                  desc="Yellow line that follows your cursor"
                  active={settings.readingGuide}
                  onClick={() => update({ readingGuide: !settings.readingGuide })}
                />
              </Section>

              <div className="text-[11px] text-gray-500 leading-relaxed border-t border-gray-200 pt-5">
                Settings are saved on this device. We're committed to WCAG 2.1 AA. If you encounter
                a barrier, please{" "}
                <a href="/contact" className="text-[#0A192F] underline hover:text-[#D4AF37]">
                  contact us
                </a>
                .
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Accessibility Statement modal */}
      {showStatement && (
        <div
          className="a11y-widget-root fixed inset-0 z-[10000] flex items-center justify-center p-4"
          role="dialog"
          aria-label="Accessibility Statement"
          data-testid="accessibility-statement-modal"
        >
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowStatement(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="bg-[#0A192F] text-white px-6 py-5 flex items-center justify-between sticky top-0">
              <h2 className="font-display font-bold text-base uppercase tracking-widest">
                Accessibility Statement
              </h2>
              <button
                onClick={() => setShowStatement(false)}
                aria-label="Close statement"
                data-testid="accessibility-statement-close"
                className="text-white hover:text-[#D4AF37] p-2"
              >
                <X size={22} />
              </button>
            </div>
            <div className="p-6 sm:p-10">
              <AccessibilityStatementContent />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-display font-bold text-[11px] uppercase tracking-[0.3em] text-[#0A192F] mb-4">
      {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Toggle = ({ icon: Icon, label, desc, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={`w-full flex items-start gap-3 border p-3 text-left transition-colors ${
      active
        ? "border-[#D4AF37] bg-[#FFF9E6]"
        : "border-gray-200 bg-white hover:border-[#D4AF37]"
    }`}
  >
    {Icon && (
      <Icon
        size={18}
        className={active ? "text-[#D4AF37]" : "text-[#0A192F]"}
      />
    )}
    <div className="flex-1">
      <div className="font-display font-bold text-xs text-[#0A192F] uppercase tracking-wide">
        {label}
      </div>
      {desc && <div className="text-[11px] text-gray-500 mt-1">{desc}</div>}
    </div>
    <div
      className={`w-9 h-5 rounded-full relative transition-colors ${
        active ? "bg-[#0A192F]" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
          active ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </div>
  </button>
);

const StepperControl = ({ label, value, min, max, onChange, labels, testId, icon: Icon }) => {
  return (
    <div className="border border-gray-200 p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className="text-[#0A192F]" />}
        <div className="font-display font-bold text-xs text-[#0A192F] uppercase tracking-wide flex-1">
          {label}
        </div>
        <div className="text-[11px] text-gray-500">{labels?.[value - min] ?? value}</div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          aria-label={`Decrease ${label}`}
          data-testid={`${testId}-decrease`}
          className="w-9 h-9 border border-gray-300 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#0A192F]"
        >
          <Minus size={14} />
        </button>
        <div className="flex-1 flex gap-1">
          {Array.from({ length: max - min + 1 }, (_, i) => (
            <div
              key={i}
              className={`flex-1 h-2 ${
                i + min <= value ? "bg-[#D4AF37]" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          aria-label={`Increase ${label}`}
          data-testid={`${testId}-increase`}
          className="w-9 h-9 border border-gray-300 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#0A192F]"
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
};

const SegmentedControl = ({ label, value, options, onChange, testId }) => (
  <div className="border border-gray-200 p-3" data-testid={testId}>
    <div className="font-display font-bold text-xs text-[#0A192F] uppercase tracking-wide mb-3">
      {label}
    </div>
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            data-testid={`${testId}-${opt.value}`}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-display uppercase tracking-wide font-bold border transition-colors ${
              active
                ? "border-[#D4AF37] bg-[#0A192F] text-white"
                : "border-gray-200 bg-white text-[#0A192F] hover:border-[#D4AF37]"
            }`}
          >
            {Icon && <Icon size={12} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default AccessibilityWidget;
